import { Box, Button, Icon, PlainButton, PlainLink, Text } from '@customerio/pluma-components/react';
import { useTrackEvent } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import * as styles from './index.css';
import { toggle } from '@/features/sidebar';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { useSupportChat } from '@/layouts/authenticated/components/top-navigation/components/support-chat/hook';

interface RocketFooterProps {
  productTourHref?: ((workspaceId: number | null) => string) | null;
  workspaceId: number | null;
  shouldDisplayUpgradeButton?: boolean;
  onDismiss?: () => void;
}

export function RocketFooter({
  productTourHref,
  workspaceId,
  shouldDisplayUpgradeButton,
  onDismiss,
}: RocketFooterProps) {
  const track = useTrackEvent();
  const safeNavigate = useSafeNavigate();
  const supportChat = useSupportChat();

  const handleStartProductTourClick = (event: React.MouseEvent) => {
    track('Rocket: Clicked on the Start Product Tour link');
    event.preventDefault();
    safeNavigate(event.currentTarget.getAttribute('href') ?? '');
  };

  const handleUpgradeButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Upgrade button');
    safeNavigate(event.currentTarget.getAttribute('href') ?? '');
  };

  const handleGetHelpClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Get help link');
    if (hasFeatureFlag(AccountFeatureFlag.AiEnabled)) {
      toggle('copilot');
    } else if (supportChat.isHandoverAllowed) {
      await supportChat.openChat();
    }
  };

  const handleGiveFeedbackClick = (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Give feedback link');
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('feedback', 'true');
    safeNavigate(currentUrl.pathname + currentUrl.search + currentUrl.hash);
  };

  const handleVideoClick = (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Videos link');
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('videos', 'true');
    safeNavigate(currentUrl.pathname + currentUrl.search + currentUrl.hash);
  };

  const isProductTourEnabled = typeof productTourHref === 'function';

  const isFeedbackEnabled = !isProductTourEnabled;

  const isChatEnabled = hasFeatureFlag(AccountFeatureFlag.AiEnabled) || supportChat.isHandoverAllowed;
  return (
    <Box as="footer" className={styles.rocketContentFooter} display="flex" flexDirection="column">
      <Box p="200" pb="150" display="flex" justifyContent="space-between">
        {shouldDisplayUpgradeButton === true ? (
          <Button
            variant="primary"
            href="/settings/billing/available-plans/journeys"
            onClick={handleUpgradeButtonClick}
          >
            Upgrade
          </Button>
        ) : (
          <>
            {isProductTourEnabled ? (
              <PlainLink
                href={productTourHref(workspaceId)}
                alignItems="center"
                display="flex"
                onClick={handleStartProductTourClick}
              >
                <Icon name="cursor" mr="050" color="accent" />
                <Text size="xs">Product tour</Text>
              </PlainLink>
            ) : null}
            <PlainButton alignItems="center" display="flex" onClick={handleVideoClick}>
              <Icon name="play" mr="050" color="accent" />
              <Text size="xs">Videos</Text>
            </PlainButton>
            <PlainButton
              alignItems="center"
              display="flex"
              onClick={handleGetHelpClick}
              isDisabled={__CIO_ENV__ !== 'production' || !isChatEnabled}
            >
              <Icon name="slack" mr="050" color="accent" />
              <Text size="xs">Get help</Text>
            </PlainButton>

            {isFeedbackEnabled ? (
              <PlainButton alignItems="center" display="flex" onClick={handleGiveFeedbackClick}>
                <Icon name="heart" mr="050" color="accent" />
                <Text size="xs">Give feedback</Text>
              </PlainButton>
            ) : null}
          </>
        )}
      </Box>
      <Box display="flex" justifyContent="center" pb="100">
        <PlainButton onClick={onDismiss}>
          <Text size="xxs" color="subtle">
            Don't show this again
          </Text>
        </PlainButton>
      </Box>
    </Box>
  );
}
